'use client';
import { motion } from 'framer-motion';
import MinimalVideoPlayer from './minimal-video-player';

export default function CompanyAnalysis() {
  return (
    <section
      id="features"
      className="py-8 md:py-32 bg-white dark:bg-black relative"
    >
      {/* Background gradient */}
      <div
        className="absolute left-0 top-1/2 -translate-y-1/2 size-[500px] rounded-full opacity-10 blur-[120px] pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at center, rgba(77, 46, 245, 0.4), rgba(125, 81, 237, 0.1), transparent)',
        }}
      />

      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-10 md:mb-16 px-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 dark:text-white mb-4 md:mb-6">
              Analyzing Companies Has Never Been Easier
            </h2>
            <p className="text-lg md:text-xl text-zinc-600 dark:text-zinc-400 max-w-3xl mx-auto">
              StockPenguins empowers the individual to effortlessly dissect
              loads of complex financial data to make informed investment
              decisions - quickly and comprehensively
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8 px-4 md:px-0">
            <motion.div
              className="bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-100/50 dark:border-zinc-800/50 shadow-lg hover:shadow-xl transition-all duration-300"
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="p-4 md:p-6 border-b border-zinc-100 dark:border-zinc-800">
                <h3 className="text-base md:text-lg font-semibold text-zinc-900 dark:text-white mb-1 md:mb-2">
                  Analyze Companies with Clear, Interactive Metrics
                </h3>
                <p className="text-sm md:text-base text-zinc-600 dark:text-zinc-400">
                  Unlock detailed financial analysis with key metrics simplified
                  by clear interactive charts
                </p>
              </div>
              <div className="bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-950 dark:to-zinc-900 h-[200px] md:h-[300px] flex items-center justify-center">
                <MinimalVideoPlayer
                  videoSrc="https://d4drpytggsxye.cloudfront.net/video+6+-+analyse+companies+with+clear+metrics-1743395991035.mp4"
                  thumbnailSrc="https://d4drpytggsxye.cloudfront.net/thumbnails/video-6-frame.png"
                  title="Analyze Companies with Clear, Interactive Metrics"
                  className="shadow-xl rounded-t-none"
                  autoplay={true}
                />
              </div>
            </motion.div>

            <motion.div
              className="bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-100/50 dark:border-zinc-800/50 shadow-lg hover:shadow-xl transition-all duration-300"
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="p-4 md:p-6 border-b border-zinc-100 dark:border-zinc-800">
                <h3 className="text-base md:text-lg font-semibold text-zinc-900 dark:text-white mb-1 md:mb-2">
                  Explore Top US Companies by Key Metrics
                </h3>
                <p className="text-sm md:text-base text-zinc-600 dark:text-zinc-400">
                  Quickly see which companies are leading the market and track
                  their financial performance with ease—all in one place
                </p>
              </div>
              <div className="bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-950 dark:to-zinc-900 h-[200px] md:h-[300px] flex items-center justify-center">
                <MinimalVideoPlayer
                  videoSrc="https://d4drpytggsxye.cloudfront.net/video+7+-+top+companies+list-1743396325764.mp4"
                  thumbnailSrc="https://d4drpytggsxye.cloudfront.net/thumbnails/video-7-frame.png"
                  title="Analyze Companies with Clear, Interactive Metrics"
                  className="shadow-xl rounded-t-none"
                  autoplay={true}
                />
              </div>
            </motion.div>

            <motion.div
              className="bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-100/50 dark:border-zinc-800/50 shadow-lg hover:shadow-xl transition-all duration-300"
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="p-4 md:p-6 border-b border-zinc-100 dark:border-zinc-800">
                <h3 className="text-base md:text-lg font-semibold text-zinc-900 dark:text-white mb-1 md:mb-2">
                  Easily Compare Companies Across Sectors and Peers
                </h3>
                <p className="text-sm md:text-base text-zinc-600 dark:text-zinc-400">
                  Make the right investing choices by understanding how
                  companies stack up against each other in terms of revenue,
                  profitability, valuation and more
                </p>
              </div>
              <div className="bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-950 dark:to-zinc-900 h-[200px] md:h-[300px] flex items-center justify-center">
                <MinimalVideoPlayer
                  videoSrc="https://d4drpytggsxye.cloudfront.net/video+8+-+compare+page-1743397247761.mp4"
                  thumbnailSrc="https://d4drpytggsxye.cloudfront.net/thumbnails/video-8-frame.png"
                  title="Analyze Companies with Clear, Interactive Metrics"
                  className="shadow-xl rounded-t-none"
                  autoplay={true}
                />
              </div>
            </motion.div>

            <motion.div
              className="bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-100/50 dark:border-zinc-800/50 shadow-lg hover:shadow-xl transition-all duration-300"
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="p-4 md:p-6 border-b border-zinc-100 dark:border-zinc-800">
                <h3 className="text-base md:text-lg font-semibold text-zinc-900 dark:text-white mb-1 md:mb-2">
                  Financial Statement Deep-Dive
                </h3>
                <p className="text-sm md:text-base text-zinc-600 dark:text-zinc-400">
                  Analyze a company&apos;s Income Statement, Balance Sheet, and
                  Cash Flow Statement with interactive, exportable graphs.
                </p>
              </div>
              <div className="bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-950 dark:to-zinc-900 h-[200px] md:h-[300px] flex items-center justify-center">
                <MinimalVideoPlayer
                  videoSrc="https://d4drpytggsxye.cloudfront.net/video+9+-+financial+statement+deep+dive+graphing-1743397975668.mp4"
                  thumbnailSrc="https://d4drpytggsxye.cloudfront.net/thumbnails/video-9-frame.png"
                  title="Financial Statement Deep-Dive"
                  className="shadow-xl rounded-t-none"
                  autoplay={true}
                />
              </div>
            </motion.div>
          </div>

          {/* <motion.div
            className="mt-16 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Link href="/features">
              <Button className="text-sm h-12 px-8 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white dark:bg-[#4d2ef5] dark:hover:bg-[#3f25cc] dark:text-white font-medium shadow-lg shadow-zinc-900/10 dark:shadow-[#4d2ef5]/20">
                Explore all features
              </Button>
            </Link>
          </motion.div> */}
        </div>
      </div>
    </section>
  );
}
